/**
 * Right by You claims on the profile surfaces
 * ------------------------------------------------------------------
 * The profile pages show a customer where their Right by You requests stand
 * without making them open the app first:
 *
 *   - the profile hub gets a "Right by You requests" card, one row per claim
 *     ([data-rby-claims], printed hidden by snippets/profile-data.liquid);
 *   - on Order History, every card a claim names carries a status pill beside
 *     the order's own pill ([data-order-name], written by
 *     snippets/profile-order-card.liquid).
 *
 * The reader is GET <rbyOrigin>/rby/claims.json with the app's session cookie
 * — the app's own origin, never an App Proxy path, because the proxy strips
 * that cookie. The origin comes from the one theme setting (Theme settings →
 * Right by You app) printed into the boot config JSON by
 * snippets/pf-profile-boot.liquid, the same arrangement as the profile app's
 * base URL.
 *
 * States: blank origin -> nothing loads; 401 -> the hub card becomes a
 * compact sign-in prompt (the journey page runs the sign-in bridge); network
 * error or 5xx -> stay hidden, like any other not-connected surface; 200 with
 * no claims -> stay hidden. The payload carries no customer identifiers — just
 * order name, product titles, timestamps and a display status.
 */
(function () {
  "use strict";

  var SESSION = window.PFProfileSession;
  if (!SESSION) return;

  var origin = String(SESSION.config().rbyOrigin || "").replace(/\/+$/, "");
  if (!origin) return;

  var FEED = origin + "/rby/claims.json";

  function text(value) {
    return value === undefined || value === null ? "" : String(value);
  }

  function requestedDate(iso) {
    var date = new Date(text(iso));
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }

  /* Mirrors CLAIM_NEXT_ACTION_LABEL in the app's journey-html module. */
  var ACTION_LABEL = {
    resume: "Continue your request",
    "choose-again": "Choose again",
    status: "View status",
  };

  function pill(status) {
    var node = document.createElement("span");
    node.className =
      "pf-orders__status pf-orders__status--rby-" + text(status.pill || "ready");
    node.textContent = text(status.label || "Requested");
    return node;
  }

  function claimRow(claim) {
    var status = claim.status || {};
    var link = document.createElement("a");
    link.className = "pf-profile__row";
    link.href = text(claim.journeyUrl) || origin + "/rby/journey";

    var copy = document.createElement("span");
    copy.className = "pf-profile__grow";
    var name = document.createElement("strong");
    name.textContent = text(claim.orderName) || "Right by You request";
    copy.appendChild(name);

    var detail = [];
    var when = requestedDate(claim.submittedAt);
    if (when) detail.push("Requested " + when);
    var action = ACTION_LABEL[status.nextAction];
    if (action) detail.push(action);
    if (detail.length) {
      var small = document.createElement("small");
      small.textContent = detail.join(" · ");
      copy.appendChild(small);
    }

    link.appendChild(copy);
    link.appendChild(pill(status));
    return link;
  }

  function signInRow() {
    var link = document.createElement("a");
    link.className = "pf-profile__row";
    link.href = origin + "/rby/journey";
    var copy = document.createElement("span");
    copy.className = "pf-profile__grow";
    var label = document.createElement("strong");
    label.textContent = "Sign in to see your Right by You requests";
    copy.appendChild(label);
    link.appendChild(copy);
    return link;
  }

  /* Both the mobile and the desktop hub carry the section; fill every copy. */
  function fillHub(buildRows) {
    var sections = document.querySelectorAll("[data-rby-claims]");
    Array.prototype.forEach.call(sections, function (section) {
      var list = section.querySelector("[data-rby-claims-list]");
      if (!list) return;
      var rows = buildRows();
      if (!rows.length) {
        section.hidden = true;
        return;
      }
      list.textContent = "";
      rows.forEach(function (row) {
        list.appendChild(row);
      });
      section.hidden = false;
    });
  }

  /* Hydration rebuilds the order cards from a template, so the pill markers
     vanish with the old cards — hence this runs again on pf:profile:hydrated
     and idempotency is the [data-rby-pill] marker a rebuilt card lacks. */
  function flagOrders(claims) {
    var byOrder = {};
    claims.forEach(function (claim) {
      var name = text(claim.orderName);
      if (name && !byOrder[name]) byOrder[name] = claim;
    });
    var cards = document.querySelectorAll(
      ".pf-orders__card[data-order-name], .pf-orders__desktop-card[data-order-name]",
    );
    Array.prototype.forEach.call(cards, function (card) {
      if (card.querySelector("[data-rby-pill]")) return;
      var claim = byOrder[card.getAttribute("data-order-name")];
      if (!claim) return;
      var marker = pill(claim.status || {});
      marker.setAttribute("data-rby-pill", "");
      var host = card.querySelector(".pf-orders__id") || card;
      host.appendChild(marker);
    });
  }

  var claims = null;

  fetch(FEED, {
    credentials: "include",
    headers: { accept: "application/json" },
  })
    .then(function (response) {
      if (response.status === 401) {
        fillHub(function () {
          return [signInRow()];
        });
        return;
      }
      if (!response.ok) return; /* 5xx: not connected — stay hidden */
      return response.json().then(function (body) {
        claims = (body && body.claims) || [];
        fillHub(function () {
          return claims.map(claimRow);
        });
        flagOrders(claims);
      });
    })
    .catch(function () {
      /* Offline/BLOCKED: the surfaces simply stay hidden. */
    });

  document.addEventListener("pf:profile:hydrated", function () {
    if (claims) flagOrders(claims);
  });
})();
